import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import { selectCustomStyles } from '../../../utils/Select'
import { graphqlRequestClient, queryClient } from '../../../queries/client'
import { kFormatter, kFormatterRupee } from '../../../utils/CurrencyFormatter'
import { weightageLV } from '../../../types/exitEnabler'
import Toast, { ToastType } from '../../../utils/toast'
import { EditEePortfolioMutation, useEditEePortfolioMutation, useGetEePortfoliosQuery } from '../../../generated/graphql'
import { randomWeights } from './EEPortfolios'
import { useIsMutating } from 'react-query'

const EditEEPortfolio = ({ portfolio: port }: any) => {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Array<any>>([])
  const [showCalc, setShowCalc] = useState<boolean>(false)

  const goBack = () => navigate('/exitEnabler/eePortfolios')

  const editMutate = useEditEePortfolioMutation<Error>(
    graphqlRequestClient,
    {
      onSuccess: (data: EditEePortfolioMutation) => {
        queryClient.invalidateQueries(useGetEePortfoliosQuery.getKey())
        Toast(data.editEEPortfolio.message, ToastType.success)
        navigate('/exitEnabler/eePortfolios')
      },
      onError: (error: any) => Toast(error.response.errors[0].message, ToastType.error),
    },
    {}
  )

  const nullArr: number[] = []

  const editForm = useFormik({
    initialValues: {
      id: -1,
      expAmount: 100000000,
      minAmount: 10000000,
      numBuckets: 10,
      weightage: '',
      weights: nullArr,
      expPPS: nullArr,
      numShares: nullArr,
      name: '',
    },
    validationSchema: Yup.object().shape({
      expAmount: Yup.number().required('Enter the expected amount'),
      minAmount: Yup.number().required('Enter the minimum amount'),
      // weightage: Yup.string().required('Select weightage'),
      name: Yup.string().required('Give your portfolio a name').min(3, 'Name must be at least 3 characters'),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: function (values) {
      const editData = {
        id: port.id,
        name: values.name,
        expAmount: values.expAmount,
        minAmount: values.minAmount,
        numBuckets: values.numBuckets,
        companies: companies.map((c: any, i) => {
          const cd = {
            id: c.id,
            name: c.name,
            cin: c.cin,
            sharePrice: c.sharePrice,
            numShares: values.numShares && values.numShares[i],
            weight: values.weights && values.weights[i],
            expPPS: values.expPPS && values.expPPS[i],
          }
          return cd
        }),
      }
      if (Number(values.weights.reduce((s, c) => s + c, 0).toFixed(2)) !== 100) Toast('Total weightage must be exactly 100', ToastType.error)
      else {
        console.log('~  EditEEPortfolio  editData:', editData)
        editMutate.mutate({ input: editData })
      }
    },
  })

  useEffect(() => {
    if (port) {
      setCompanies(port.companies)
      editForm.setFieldValue('name', port.name)
      editForm.setFieldValue('expAmount', Number(port.expAmount))
      editForm.setFieldValue('minAmount', Number(port.minAmount))
      editForm.setFieldValue('numBuckets', Number(port.numBuckets))
      editForm.setFieldValue(
        'weights',
        port.companies.map((c: any) => c.weight)
      )
      editForm.setFieldValue(
        'expPPS',
        port.companies.map((c: any) => c.expPPS)
      )
      editForm.setFieldValue(
        'numShares',
        port.companies.map((c: any) => c.numShares)
      )
      setShowCalc(true)
    }
  }, [port])

  const handleWeight = (w: number, i: number) => {
    let oW = editForm.values.weights
    oW[i] = w
    editForm.setFieldValue('weights', oW)

    let oNS = editForm.values.numShares
    const noSh = (editForm.values.minAmount * (w / 100)) / editForm.values.expPPS[i]
    oNS[i] = Math.round(noSh)
    editForm.setFieldValue('numShares', oNS)
  }

  const handleExpPPS = (sp: number, i: number) => {
    let oSP = editForm.values.expPPS
    oSP[i] = sp
    editForm.setFieldValue('expPPS', oSP)

    let oNS = editForm.values.numShares
    const noSh = (editForm.values.minAmount * (editForm.values.weights[i] / 100)) / sp
    oNS[i] = Math.round(noSh)
    editForm.setFieldValue('numShares', oNS)
  }

  const handleExpAmount = (v: any) => {
    setShowCalc(false)
    const val = parseFloat(v.replaceAll(',', '')) || 0
    editForm.setFieldValue('expAmount', val)
    editForm.setFieldValue(
      'numBuckets',
      val % editForm.values.minAmount > 0 ? Math.floor(val / editForm.values.minAmount) + 1 : Math.floor(val / editForm.values.minAmount)
    )
  }

  const handleMinAmount = (v: any) => {
    setShowCalc(false)
    const val = parseFloat(v.replaceAll(',', '')) || 0
    editForm.setFieldValue('minAmount', val)
    editForm.setFieldValue(
      'numBuckets',
      editForm.values.expAmount % val > 0 ? Math.floor(editForm.values.expAmount / val) + 1 : Math.floor(editForm.values.expAmount / val)
    )
  }

  const handleWeightage = (v: any) => {
    setShowCalc(false)
    editForm.setFieldValue('weightage', v)
    editForm.setFieldValue(
      'weights',
      v === 'equal' ? Array(companies.length).fill(Number((100 / companies.length).toFixed(2))) : randomWeights(companies.length)
    )
    editForm.setFieldValue(
      'expPPS',
      companies.map((c) => c.sharePrice)
    )
  }

  const calcBuckets = () => {
    if (editForm.values.weightage) {
      let oNS = editForm.values.numShares
      companies.map((c, i) => {
        const noSh = (editForm.values.minAmount * (editForm.values.weights[i] / 100)) / editForm.values.expPPS[i]
        oNS[i] = Math.round(noSh)
      })
      editForm.setFieldValue('numShares', oNS)
      setShowCalc(true)
    } else editForm.setTouched({ ...editForm.touched, weightage: true })
  }

  return (
    <div className='row'>
      <form onSubmit={editForm.handleSubmit}>
        <div className='card mb-5'>
          <div className='card-body'>
            <div className='d-flex align-items-center mb-2'>
              <p className='text-gray-800 text-hover-primary fs-2 me-1 mb-0'>Edit Portfolio</p>
            </div>
            <div className='fs-6 pe-2'>
              <p className='d-flex align-items-center text-gray-400 text-hover-primary me-5 mb-5'>
                This is a invite only special module to empower you to create an exit portfolios from your investments
              </p>
              {!companies ? (
                <p className='d-flex align-items-center mb-1'>
                  First, filter companies from your investments under 'Model My Exit' or your watchlist companies under 'Model Watchlist Exit'
                  <br />
                  Then click 'Create Portfolio' to create your first portfolio for sale
                </p>
              ) : (
                <div className='row fv-row'>
                  <div className='col-3 px-2'>
                    <label className='form-label required'>Portfolio Name</label>
                    <input
                      type='text'
                      className='form-control me-5'
                      id='name'
                      placeholder='Enter portfolio name'
                      {...editForm.getFieldProps('name')}
                    />
                    <div className='text-danger mt-2'>
                      {editForm.touched.name && editForm.errors.name && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{editForm.errors.name}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='col-3 px-2'>
                    <label className='form-label required'>Expected Amount</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Enter the expected amount'
                      {...editForm.getFieldProps('expAmount')}
                      title={kFormatter(editForm.values.expAmount)}
                      onChange={(e) => handleExpAmount(e.target.value)}
                      value={Intl.NumberFormat('en-IN').format(editForm.values.expAmount)}
                    />
                    <span className='d-flex flex-end text-muted pe-5'>{kFormatter(editForm.values.expAmount)}</span>
                    <div className='text-danger mt-2'>
                      {editForm.touched.expAmount && editForm.errors.expAmount && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{editForm.errors.expAmount}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='col-3 px-2'>
                    <label className='form-label required'>Minimum Amount</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Enter the min amount'
                      {...editForm.getFieldProps('minAmount')}
                      title={kFormatter(editForm.values.minAmount)}
                      onChange={(e) => handleMinAmount(e.target.value)}
                      value={Intl.NumberFormat('en-IN').format(editForm.values.minAmount)}
                    />
                    <span className='d-flex flex-end text-muted pe-5'>{kFormatter(editForm.values.minAmount)}</span>
                    <div className='text-danger mt-2'>
                      {editForm.touched.minAmount && editForm.errors.minAmount && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{editForm.errors.minAmount}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='col-3 px-2'>
                    <label className='form-label required'>Number of Buckets</label>
                    <input type='number' className='form-control' disabled {...editForm.getFieldProps('numBuckets')} />
                    {editForm.values.expAmount % editForm.values.minAmount > 0 && (
                      <span className='d-flex flex-end text-muted pe-5'>
                        Last bucket: {kFormatter(editForm.values.expAmount % editForm.values.minAmount)}
                      </span>
                    )}
                    <div className='text-danger mt-2'>
                      {editForm.touched.numBuckets && editForm.errors.numBuckets && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{editForm.errors.numBuckets}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='col-3 px-2'>
                    <label className='form-label required'>Weightage</label>
                    <Select
                      className='text-primary'
                      options={weightageLV}
                      name='weightage'
                      onChange={(e: any) => handleWeightage(e.value)}
                      styles={selectCustomStyles}
                    />
                    <span className='d-flex flex-end text-muted fs-8'>You can alter weightage in the next step</span>
                    <div className='text-danger mt-2'>
                      {editForm.touched.weightage && editForm.errors.weightage && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>{editForm.errors.weightage}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='col-3' />
                  <div className='col-3 d-flex flex-column justify-content-center align-items-center'>
                    <button type='button' className='btn btn-primary' onClick={() => calcBuckets()}>
                      Calculate
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='card p-0'>
          {companies && (
            <>
              <div className='card-header border-0 pt-5'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fs-3 mb-1'>Filtered Companies</span>
                  <span className='mt-1 fw-semibold fs-7 text-muted'>{companies.length} companies</span>
                </h3>
              </div>
              <div className='card-body py-3'>
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed dataTable no-footer'>
                    <thead>
                      <tr key='nofilter'>
                        <th className='min-w-300px'>
                          Company Name
                          <span className='text-muted d-block fs-7'>Business Name</span>
                        </th>
                        <th className='text-end'>
                          Valuation (<span style={{ fontFamily: 'Georgia' }}>₹</span>)
                        </th>
                        {showCalc && (
                          <>
                            <th className='text-end'>Shares (#)</th>
                            <th className='text-end'>
                              Amount (<span style={{ fontFamily: 'Georgia' }}>₹</span>)
                            </th>
                            <th className='text-end'>Weightage (%)</th>
                            <th className='text-end'>
                              Expected Share Price (<span style={{ fontFamily: 'Georgia' }}>₹</span>)
                            </th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map((comp: any, i: any) => {
                        return (
                          <tr key={i}>
                            <td>
                              <p className='mb-1'>{comp.name}</p>
                              <span className='text-muted d-block fs-7'>{comp.DBA}</span>
                            </td>
                            <td className='text-end'>
                              <p className='mb-1'>{kFormatter(comp.valuation)}</p>
                              <span className='text-muted d-block fs-7'>
                                {comp.valuation > 999 && kFormatterRupee(Number(comp.valuation.toString().split('.')[0]))}
                              </span>
                            </td>
                            {showCalc && editForm.values && (
                              <>
                                <td className='text-end'>
                                  <p className='mb-1'>{kFormatter(editForm.values.numShares[i])}</p>
                                  <span className='text-muted d-block fs-7'>
                                    {editForm.values.numShares[i] > 999 && kFormatterRupee(Number(editForm.values.numShares[i]))}
                                  </span>
                                </td>
                                <td className='text-end'>
                                  <p className='mb-1'>{kFormatter(editForm.values.expPPS[i] * editForm.values.numShares[i])}</p>
                                  <span className='text-muted d-block fs-7'>
                                    {editForm.values.expPPS[i] * editForm.values.numShares[i] > 999 &&
                                      kFormatterRupee(Number((editForm.values.expPPS[i] * editForm.values.numShares[i]).toString().split('.')[0]))}
                                  </span>
                                </td>
                                <td className='text-end'>
                                  <input
                                    type='number'
                                    className='form-control text-end'
                                    onChange={(e) => handleWeight(Number(e.target.value), i)}
                                    value={editForm.values.weights && editForm.values.weights[i]}
                                  />
                                  {editForm.values.weights && editForm.values.weights[i] < 0 && (
                                    <div className='text-danger mt-2'>
                                      <div className='fv-plugins-message-container'>
                                        <div className='fv-help-block'>Negative is invalid</div>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td className='text-end'>
                                  <input
                                    type='text'
                                    className='form-control text-end'
                                    title={kFormatter(editForm.values.expPPS[i])}
                                    onChange={(e) => handleExpPPS(Number(e.target.value.replaceAll(',', '')), i)}
                                    value={editForm.values.expPPS && kFormatterRupee(Number(editForm.values.expPPS[i].toString().split('.')[0]))}
                                  />
                                  {editForm.values.expPPS && editForm.values.expPPS[i] <= 0 && (
                                    <div className='text-danger mt-2'>
                                      <div className='fv-plugins-message-container'>
                                        <div className='fv-help-block'>Invalid share price</div>
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        )
                      })}
                      {showCalc && (
                        <tr key='totals'>
                          <td colSpan={3} className='text-end'>
                            <p className='mb-1'>
                              Total Amount per Bucket (<span style={{ fontFamily: 'Georgia' }}>₹</span>)
                            </p>
                          </td>
                          <td className='text-end'>
                            <p className='mb-1'>
                              {kFormatter(companies.reduce((s, c, i) => s + editForm.values.expPPS[i] * editForm.values.numShares[i], 0))}
                            </p>
                            <span className='text-muted d-block fs-7'>
                              {kFormatterRupee(
                                Number(
                                  companies
                                    .reduce((s, c, i) => s + editForm.values.expPPS[i] * editForm.values.numShares[i], 0)
                                    .toString()
                                    .split('.')[0]
                                )
                              )}
                            </span>
                          </td>
                          <td className='text-end'>
                            <p className='mb-1'>{editForm.values.weights.reduce((s, c) => s + c, 0).toFixed(2)}</p>
                            {Number(editForm.values.weights.reduce((s, c) => s + c, 0).toFixed(2)) !== 100 && (
                              <span className='text-danger d-block fs-7'>Total must be 100.00</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          <hr className='m-0 border-gray-500'></hr>
          <div className='p-5 d-flex flex-row justify-content-end align-items-start'>
            <div className='px-2'>
              <button type='button' className='btn btn-sm btn-secondary me-3' onClick={() => goBack()}>
                Cancel
              </button>
              <button type='submit' className='btn btn-primary' disabled={useIsMutating() > 0 || !showCalc}>
                {useIsMutating() === 0 && 'Save'}
                {useIsMutating() > 0 && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export { EditEEPortfolio }
